import _ from 'lodash';
import { createContext, useContext, useEffect, useReducer } from 'react';
import { Container, Header } from "semantic-ui-react";
import { AppFormsContext } from '../../../cnr/contexts/AppFormsContext';
import { appFormHandlers, appFormInitialState, appFormReducer } from '../../../cnr/reducers/AppFormReducer';
import UiSaveButtons from '../../../components/buttons/UiSaveButtons';
import { isItemReady } from '../../../validation/validation';
import FullPageWrapper from '../../../wrappers/FullPageWrapper';
import Wrapper, { wrapperTypes } from '../../../wrappers/Wrapper';
import NoData from '../../alerts/NoData';
import FormForm from '../../forms/FormForm';
import { AppFormWizard } from './AppFormWizard';
import { useAppForm } from 'global/cnr/reducers/useAppForm';

export const AppFormContext = createContext();

/**
 * 
 * @param {object} props 
 * @param {enum} - formType: the type of form from the `appFormTypes` enum
 * @param {function} - handleUpdateParentData: function to update the parent component
 * @param {object} - parentData: data from the parent component
 * @param {array} - propIgnores: list of props to ingnore, if any
 * @param {boolean} - useWizard: whether the form should be separated into a wizard
 * @returns Form or Wizard
 */
const AppForm = (props) => {

  const {
    dataPropType,
    _formAttributes,
    _header,
    appFormItems,
    appFormType,
    appFormTypes,
    confirmation,
    css,
    data_parent,
    defaultOptions,
    handleCancel,
    handleDelete,
    handlePreview,
    handleSaveCaption,
    handleSaveIcon,
    handleSaveColor,
    handleUpdateParentData,
    ignoreItemStatus,
    keepParent,
    previewCaption,
    propIgnores,
    topperCaption,
    topperCaption2,
    updating,
    useFullPageWrapper,
    useWizard,
    useMvc,
    viewOnly,
    vit,
  } = props

  // appFormsContext
  const appFormsContext = useContext(AppFormsContext);
  const { appForms_state } = appFormsContext ?? {}
  const { appForms } = appForms_state ?? {}

  // appForms
  const init_state = { appForms, data_parent, appFormType, appFormTypes, appFormItems, propIgnores, vit }
  const [appForm_state, appForm_dispatch] = useReducer(appFormReducer, appFormInitialState(init_state));
  const appForm_handlers = appFormHandlers(appForm_dispatch)

  const { currentAppForms, formProps, noProps, data_form } = appForm_state ?? {}

  const form_handlers = props.form_handlers ? props.form_handlers : { handleFormDataChange_local: appForm_handlers.handleLocalPropChange }

  const { formPropz } = useAppForm({
    dataPropType,
    appFormType,
    appFormItems,
    data_form: data_parent,
    vit,
    propIgnores,
    appFormTypes,
    form_handlers
  })

  const { viewProps } = formProps ?? {}

  const vpCount = viewProps ? Object.keys(viewProps).length : 0

  const handleSave = () => props.handleSave(data_form)

  useEffect(() => {
    if (appForms) {
      appForm_handlers.handleInit_form(init_state, formPropz)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [currentAppForms, appFormItems, appFormType, defaultOptions, data_parent]);

  /** updates the parent component when the data_form changes */
  useEffect(() => {
    if (viewProps && data_form) {
      if (keepParent) {
        const dfi = { [appFormType]: data_form };
        if (!_.isEqual(dfi, data_parent)) { // Prevent unnecessary updates
          handleUpdateParentData && handleUpdateParentData(dfi, isItemReady(viewProps, dfi), vpCount);
        }
      } else {
        if (!_.isEqual(data_form, data_parent)) { // Prevent unnecessary updates
          handleUpdateParentData && handleUpdateParentData(data_form, isItemReady(viewProps, data_form));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [data_form]);

  const form = () => <FormForm
    form_handlers={form_handlers}
    formAttributes={_formAttributes}
    formProps={formProps}
    noSort={true}
    viewOnly={viewOnly}
    ignoreItemStatus={ignoreItemStatus}
  />

  const formView = () => {
    const segs = _.map(viewProps, (vp, key) => {
      const data = data_form[key]
      return <Container fluid style={{ padding: '1em' }}>
        <Header as='h4'>{_.startCase(key)}</Header>
        <p>
          {data ? data : 'No ' + _.startCase(key)}
        </p>
      </Container>
    })
    return segs
  }

  const footer = () => <UiSaveButtons
    delete={handleDelete ? { oc: handleDelete } : null}
    preview={handlePreview ? { oc: handlePreview, caption: previewCaption ? previewCaption : null } : null}
    save={{ oc: handleSave, caption: handleSaveCaption ? handleSaveCaption : null, disabled: !data_form, color: handleSaveColor, icon: handleSaveIcon }}
    cancel={{ oc: handleCancel, caption: 'Cancel' }}
  ></UiSaveButtons>

  const formWrapper = () => <Wrapper
    header={_header && _header}
    content={viewOnly ? formView() : form()}
    footer={footer()}
    wrapperType={wrapperTypes.paddedFooter}
    updating={updating}
    confirmation={confirmation}
    css={css}
  />

  const fullPageWrapper = () => <FullPageWrapper
    content={formWrapper()}
    handleCancel={handleCancel}
    topperCaption={topperCaption}
    topperCaption2={topperCaption2}
    updating={updating}
  />

  const content_form = () => {
    if (useWizard) {
      return <AppFormWizard
        form_handlers={form_handlers}
        formData={data_form}
        handleCancel={handleCancel}
        topperCaption={topperCaption}
        topperCaption2={topperCaption2}
        updating={updating}
        viewProps={viewProps}
        handlePreview={props.handlePreview}
        handleSave={props.handleSave}
        handleSaveCaption={props.handleSaveCaption}
        useMvc={useMvc}
      />
    } else {
      if (props.handleSave) {
        if (useFullPageWrapper) {
          return fullPageWrapper()
        } else {
          return formWrapper()
        }
      } else {
        return viewOnly ? formView() : form()
      }
    }
  }

  if (formProps && viewProps) {
    return <AppFormContext.Provider value={{ appForm_state, appForm_handlers }}>
      {content_form()}
    </AppFormContext.Provider>
  }

  if (noProps) { return <NoData fullCaption={"No form props have been set for `" + appFormType + "`. Please update the `AppForms`."}></NoData> }

  return <div>?</div>

}

export default AppForm